import { Ng2StateDeclaration } from "@uirouter/angular";
import { Ng1StateDeclaration } from "@uirouter/angularjs";
import { editUserDetailsStates, userDetailsStates } from "@gtmhub/configuration/configuration.states.utils";
import { modal as ng1Modal } from "@gtmhub/core/routing";
import { localize } from "@gtmhub/localization";
import { attachNg1StateDependency, attachNg2StateDependency, featureAvailable, featureToggleEnabled, featuresAvailable } from "@gtmhub/resolve-guards";
import { drawer } from "@webapp/core/routing/utils/drawer.routing.utils";
import { ModuleFlag } from "@webapp/feature-toggles/models/feature-module.models";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { goalFutureStates, metricFutureStates } from "@webapp/okrs/okrs.future-states";
import { ManageReflectionsComponent } from "@webapp/reflections/components/manage-reflections/manage-reflections.component";

class TeamsStateFactory {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _form(base: string): Ng1StateDeclaration {
    return ng1Modal({
      name: `${base}._form`,
      view: "teamform",
      windowClass: "create-team medium",
      backdropClass: "dark-backdrop",
      abstract: true,
      backdrop: true,
    });
  }

  public form(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._form.form`,
      url: "form/:teamId/?:avatar-selector-only&parentTeamId",
      params: {
        id: null,
      },
      data: {
        trackingPage: "Team Form",
      },
      views: {
        "teamform@": {
          template: require("../teams/views/form.html"),
          controller: "TeamFormCtrl",
        },
      },
    };
  }

  public teams(): Ng1StateDeclaration {
    return {
      name: "gtmhub.teams",
      data: {
        requiresLogin: true,
        titleKey: "teams_cap",
        trackingPage: "Teams Cap",
        pageBackgroundColor: "#fff",
        newLayout: true,
      },
      params: {},
      url: "/teams/",
      template: require("../teams/views/list.html"),
      controller: "TeamsCtrl",
    };
  }

  /**
   * This is a placeholder state so that we can lazy-load the teams
   * state (see people.future-states.ts)
   */
  public list(): Ng1StateDeclaration {
    return {
      name: "gtmhub.teams.list",
      url: "",
      redirectTo: "gtmhub.teams",
    };
  }

  public organizationChart(): Ng1StateDeclaration {
    return attachNg1StateDependency(
      {
        name: "gtmhub.organization_chart",
        data: {
          requiresLogin: true,
          titleKey: "organization",
          trackingPage: "Organization",
        },
        url: "/organization/",
        params: {},
        template: require("../teams/views/organization-chart.html"),
        controller: "OrganizationChartCtrl",
      },
      featureAvailable("people.organization"),
      featureToggleEnabled(FeatureFlag.OrganizationChartV2)
    );
  }

  public organizationChartV3(): Ng1StateDeclaration {
    return attachNg1StateDependency(
      {
        name: "gtmhub.organization_chartv3",
        data: {
          requiresLogin: true,
          titleKey: "organization",
          trackingPage: "Organization",
        },
        url: "/organization-v3/",
        component: "orgChartV3",
      },
      featureAvailable("people.organization"),
      featureToggleEnabled(FeatureFlag.OrganizationChartV3)
    );
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _manage(base: string): Ng1StateDeclaration {
    return ng1Modal({
      name: `${base}._manage`,
      view: "modalview",
      data: {
        trackingPage: "User Teams Manage",
      },
      windowClass: "user-teams-modal xl-large",
      abstract: true,
      backdrop: true,
    });
  }

  public manage(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._manage.manage`,
      url: "manage/",
      data: {
        trackingPage: "Manage Teams",
      },
      views: {
        "modalview@": {
          component: "bulkEditTeamMembers",
        },
      },
    };
  }
}

class BadgesStateFactory {
  public badges(): Ng1StateDeclaration {
    return attachNg1StateDependency(
      {
        name: "gtmhub.badges",
        data: {
          requiresLogin: true,
          trackingPage: "Manage Badges",
          titleKey: "manage_badges",
        },
        url: "/badges/",
        template: require("../badges/views/badges.html"),
        controller: "BadgesCtrl",
      },
      featureToggleEnabled(ModuleFlag.Badges)
    );
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _form(): Ng1StateDeclaration {
    return ng1Modal({
      name: "gtmhub.badges._form",
      view: "badgeform",
      data: {
        trackingPage: "Badge Form",
      },
      abstract: true,
      windowClass: "create-badge medium",
    });
  }

  public form(): Ng1StateDeclaration {
    return {
      name: "gtmhub.badges._form.form",
      url: ":badgeId/form/",
      data: {
        trackingPage: "Badge Form Details",
      },
      params: {
        id: null,
      },
      views: {
        "badgeform@": {
          template: require("../badges/views/form.html"),
          controller: "BadgeFormCtrl",
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _badgeAssignment(base: string): Ng1StateDeclaration {
    return ng1Modal({
      name: `${base}._badge`,
      view: "awardbadge",
      abstract: true,
      windowClass: "single-badge-preview medium",
    });
  }

  public badgeAssignment(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._badge.badge`,
      url: "badge/:badgeAssignmentId/",
      data: {
        trackingPage: "Badge Assignment",
      },
      views: {
        "awardbadge@": {
          template: require("../badges/views/badge.html"),
          controller: "BadgeCtrl",
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _badgeTemplates(): Ng1StateDeclaration {
    return ng1Modal({
      name: "gtmhub.badges._badgeTemplates",
      view: "badgetemplatesview",
      abstract: true,
      windowClass: "badge-templates modal-small",
    });
  }

  public badgeTemplates(): Ng1StateDeclaration {
    return {
      name: "gtmhub.badges._badgeTemplates.badgeTemplates",
      url: "badge-templates/",
      data: {
        trackingPage: "Badge Templates",
      },
      params: {
        id: null,
      },
      views: {
        "badgetemplatesview@": {
          template: require("../badges/views/templates.html"),
          controller: "BadgeTemplatesCtrl",
        },
      },
    };
  }

  public approve(): Ng1StateDeclaration {
    return attachNg1StateDependency(
      {
        name: "gtmhub.approveBadges",
        data: {
          titleKey: "approve_badges",
          trackingPage: "Approve Badges",
        },
        url: "/badges/approve/",
        template: require("../badges/views/approve.html"),
        controller: "ApproveBadgesCtrl",
      },
      featureToggleEnabled(ModuleFlag.Badges)
    );
  }
}

class EmployeesStateFactory {
  public employees(): Ng1StateDeclaration {
    return {
      name: "gtmhub.employees",
      data: {
        requiresLogin: true,
        titleKey: "employees",
        trackingPage: "Employees",
        pageBackgroundColor: "#fff",
      },
      url: "/employees/",
      template: require("../employees/views/list.html"),
      controller: "EmployeesCtrl",
    };
  }

  /**
   * This is a placeholder state so that we can lazy-load the employees
   * state (see people.future-states.ts)
   */
  public list(): Ng1StateDeclaration {
    return {
      name: "gtmhub.employees.list",
      url: "",
      redirectTo: "gtmhub.employees",
    };
  }
}

const teamProfileState = (base: string): Ng1StateDeclaration => ({
  name: `${base}.team`,
  views: {
    "@gtmhub": "teamProfile",
  },
  url: ":teamId/team-details/?focusSelectManagerField&activeTab&cadenceStart&memberId&published&targetId&commentId",
  data: {
    reflections: true,
  },
  params: {
    activeTab: { dynamic: true },
    cadenceStart: { dynamic: true },
    memberId: { dynamic: true },
    expandCheckIns: { dynamic: true },
    published: { dynamic: true },
    note: { dynamic: true },
    targetId: { dynamic: true },
    commentId: { dynamic: true },
  },
});

const teamReflectionState = (base: string): Ng2StateDeclaration => {
  return attachNg2StateDependency(
    drawer({
      name: `${base}.reflection`,
      url: "manage-reflections/",
      data: {
        trackingPage: "Manage reflections",
      },
      component: ManageReflectionsComponent,
      title: localize("manage_check_ins_title"),
      size: "large",
    }),
    featuresAvailable(ModuleFlag.Reflections)
  );
};

const employeeState = (base: string): Ng1StateDeclaration =>
  ng1Modal({
    name: `${base}.employee`,
    data: {
      trackingPage: "Employee Details",
    },
    url: ":employeeId/employee-details/",
    component: "employeeProfile",
    windowClass: "profile-sidebar",
  });

const teamsStateFactory = new TeamsStateFactory();
const badgesStateFactory = new BadgesStateFactory();
const employeesStateFactory = new EmployeesStateFactory();

export const teamStates = (base: string): (Ng1StateDeclaration | Ng2StateDeclaration)[] => [
  teamProfileState(base),
  employeeState(`${base}.team`),
  teamReflectionState(`${base}.team`),
  badgesStateFactory._badgeAssignment(`${base}.team`),
  badgesStateFactory.badgeAssignment(`${base}.team`),
  teamsStateFactory._form(`${base}.team`),
  teamsStateFactory.form(`${base}.team`),
  ...goalFutureStates(`${base}.team`),
  ...metricFutureStates(`${base}.team`),
];

export const employeeStates = (base: string): (Ng1StateDeclaration | Ng2StateDeclaration)[] => [
  employeeState(base),
  badgesStateFactory._badgeAssignment(`${base}.employee`),
  badgesStateFactory.badgeAssignment(`${base}.employee`),
  ...userDetailsStates(`${base}.employee`),
  ...editUserDetailsStates(`${base}.employee`),
  ...goalFutureStates(`${base}.employee`),
  ...metricFutureStates(`${base}.employee`),
];

export const states: (Ng1StateDeclaration | Ng2StateDeclaration)[] = [
  teamsStateFactory.teams(),
  teamsStateFactory.list(),
  teamsStateFactory._form("gtmhub.teams"),
  teamsStateFactory.form("gtmhub.teams"),
  teamsStateFactory._manage("gtmhub.teams"),
  teamsStateFactory.manage("gtmhub.teams"),
  badgesStateFactory._badgeAssignment("gtmhub.teams"),
  badgesStateFactory.badgeAssignment("gtmhub.teams"),
  ...teamStates("gtmhub.teams"),
  ...employeeStates("gtmhub.teams"),

  // organization chart
  teamsStateFactory.organizationChart(),
  teamsStateFactory._form("gtmhub.organization_chart"),
  teamsStateFactory.form("gtmhub.organization_chart"),
  teamsStateFactory._manage("gtmhub.organization_chart"),
  teamsStateFactory.manage("gtmhub.organization_chart"),
  ...teamStates("gtmhub.organization_chart"),
  ...employeeStates("gtmhub.organization_chart"),

  // organization chart v3
  teamsStateFactory.organizationChartV3(),
  teamsStateFactory._form("gtmhub.organization_chartv3"),
  teamsStateFactory.form("gtmhub.organization_chartv3"),
  teamsStateFactory._manage("gtmhub.organization_chartv3"),
  teamsStateFactory.manage("gtmhub.organization_chartv3"),
  ...teamStates("gtmhub.organization_chartv3"),
  ...employeeStates("gtmhub.organization_chartv3"),

  // employees
  employeesStateFactory.employees(),
  employeesStateFactory.list(),
  badgesStateFactory._badgeAssignment("gtmhub.employees"),
  badgesStateFactory.badgeAssignment("gtmhub.employees"),
  teamsStateFactory._manage("gtmhub.employees"),
  teamsStateFactory.manage("gtmhub.employees"),
  ...teamStates("gtmhub.employees"),
  ...employeeStates("gtmhub.employees"),

  // badges
  badgesStateFactory.approve(),
  badgesStateFactory.badges(),
  badgesStateFactory._form(),
  badgesStateFactory.form(),
  // these 2 states are needed in email "*Someone* wants to award the *X* badge to *someone else*"
  badgesStateFactory._badgeAssignment("gtmhub.badges"),
  badgesStateFactory.badgeAssignment("gtmhub.badges"),
  badgesStateFactory._badgeTemplates(),
  badgesStateFactory.badgeTemplates(),
];
